// web/src/components/auth/ResendVerificationButton.tsx
//
// Inline "Didn't get the email? Resend" affordance for the SignupForm's
// post-submit "Check your email" success state, where the email address
// is already known. Renders as a button with status text — disabled
// during the 60s cooldown and shows seconds-remaining countdown.
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useResendVerification } from "./useResendVerification";

interface Props {
  email: string;
}

export function ResendVerificationButton({ email }: Props) {
  const { resend, status, cooldownRemaining, error } = useResendVerification();
  const isCoolingDown = cooldownRemaining > 0;
  const disabled = status === "sending" || isCoolingDown;
  const { t } = useTranslation("auth");

  let label = t("resend.buttonLabel");
  if (status === "sending") label = t("resend.sending");
  else if (isCoolingDown) label = t("resend.cooldown", { seconds: cooldownRemaining });
  else if (status === "sent") label = t("resend.sentInbox");

  return (
    <div className="mt-3 flex flex-col items-start gap-1.5">
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0 text-sm"
        onClick={() => resend(email)}
        disabled={disabled}
      >
        {label}
      </Button>
      {status === "error" && error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
